/**
 * The Boho replica's entrance reveals.
 *
 * WHAT THIS REPRODUCES
 *
 * The live Framer site fades and lifts every band into place -- on load for the
 * homepage hero, and on entering the viewport for everything below it. The
 * replica had none of it, and no parity gate could see that: the pixel harness
 * deliberately waits for live's animation to settle before every photograph, so
 * that live's own frame-to-frame noise does not read as a replica defect. The
 * right thing to do for a still comparison, and the reason motion went
 * unmeasured for thirty-two routes.
 *
 * WHY JAVASCRIPT AND NOT `animation-timeline: view()`
 *
 * A scroll-driven CSS animation ties progress to scroll POSITION. Live's does
 * not: the reveal is triggered by the viewport and then runs on its own clock,
 * at its own duration, and finishes whether the visitor keeps scrolling, stops,
 * or scrolls back. `view()` would rewind it on the way back up, which live
 * never does -- every reveal here is one-shot. An observer plus a transition is
 * the arrangement that behaves the way the measured one behaves.
 *
 * WHAT IT DOES NOT REPRODUCE
 *
 * Live also splits its headings into one inline-block span per character and
 * lifts them 64px in groups. That effect is NOT implemented, and deliberately:
 * the grouping rule could not be determined from the rendered evidence. On the
 * hero's h1 the groups fall on pairs of word spans; on the Sanctuary h2 they
 * fall on top-level styled runs; neither reading explains the other, and
 * neither matches the rendered line boxes. Guessing one would be inventing a
 * design rule the live site does not demonstrably follow. The measurement is
 * recorded in the audit as an open FAIL instead. See
 * docs/boho-med-spa-migration/boho-med-spa-motion-parity-findings.md.
 *
 * THE TRIGGER
 *
 * Measured: the reveal begins when the element's top has come between 6px and
 * 278px inside the viewport, depending on the surface, because Framer observes
 * a wrapper rather than the element that moves and the wrapper sits a different
 * distance away on each band. One rule has to stand for all of them, and the
 * one chosen is the low end -- fire once the top is 50px inside -- so that a
 * reveal is never still running when a visitor has finished reading past it.
 * `--boho-reveal-inset` overrides it per surface if a measurement ever justifies
 * doing so.
 */

const ROOT_CLASS = 'boho-motion';
const READY_CLASS = 'boho-motion-ready';
const DEFAULT_INSET = 50;

/** Everything the module needs from the element, read once. */
function config(el) {
  const styles = getComputedStyle(el);
  const ms = (name, fallback) => {
    const raw = styles.getPropertyValue(name).trim();
    if (!raw) return fallback;
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return fallback;
    return /ms$/.test(raw) ? value : value * 1000;
  };
  const inset = parseFloat(styles.getPropertyValue('--boho-reveal-inset'));
  return {
    inset: Number.isFinite(inset) ? inset : DEFAULT_INSET,
    duration: ms('--boho-reveal-duration', 925),
    delay: ms('--boho-reveal-delay', 0),
  };
}

/**
 * Marks one surface revealed, and drops its compositor hint once it has landed.
 *
 * `will-change: transform` promotes every reveal to its own layer, which is
 * what makes the animation smooth and what would quietly cost memory on a page
 * of a dozen bands if it were left set forever.
 */
function reveal(el, settings) {
  el.setAttribute('data-boho-revealed', '');
  window.setTimeout(() => {
    el.setAttribute('data-boho-revealed', 'settled');
  }, settings.duration + settings.delay + 60);
}

function start() {
  const surfaces = [...document.querySelectorAll('[data-boho-reveal]')];
  if (!surfaces.length) return;

  // A visitor who has asked for reduced motion gets the settled state and no
  // animation at all. Live does NOT do this -- it runs the same animation with
  // `prefers-reduced-motion: reduce` set, measured on 2026-09-21 -- so this is a
  // deliberate divergence, registered in
  // docs/boho-med-spa-migration/boho-med-spa-approved-exceptions.json.
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) {
    for (const el of surfaces) el.setAttribute('data-boho-revealed', 'settled');
    return;
  }

  const onLoad = surfaces.filter((el) => el.getAttribute('data-boho-reveal-on') === 'load');
  const onScroll = surfaces.filter((el) => el.getAttribute('data-boho-reveal-on') !== 'load');

  for (const el of onLoad) reveal(el, config(el));

  if (!('IntersectionObserver' in window)) {
    for (const el of onScroll) reveal(el, config(el));
    return;
  }

  // One observer per distinct inset, because rootMargin is a property of the
  // observer and not of the entry.
  const byInset = new Map();
  for (const el of onScroll) {
    const settings = config(el);
    const key = Math.round(settings.inset);
    if (!byInset.has(key)) byInset.set(key, []);
    byInset.get(key).push({ el, settings });
  }

  for (const [inset, members] of byInset) {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const member = members.find((m) => m.el === entry.target);
        reveal(entry.target, member ? member.settings : config(entry.target));
        observer.unobserve(entry.target);
      }
    }, { root: null, rootMargin: `0px 0px -${inset}px 0px`, threshold: 0 });
    for (const { el } of members) observer.observe(el);
  }
}

if (document.documentElement.classList.contains(ROOT_CLASS)) {
  document.documentElement.classList.add(READY_CLASS);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
